import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import ClassDrawer from "./ClassDrawer";
import { btn, card, dlong, panel } from "@/lib/ops";

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(`${s}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
const monday = (s) => { const d = new Date(`${s}T12:00:00Z`); return addDays(s, -((d.getUTCDay() + 6) % 7)); };
const STATUS = { scheduled: ["Scheduled", "text-slate-500"], completed: ["Completed", "text-emerald-600"], needs_completion: ["Needs completion", "text-amber-600"], canceled: ["Canceled", "text-red-600 line-through"], confirmed: ["Booked", "text-[#2563EB]"] };
const COLOR = { hitting: "border-l-[#2563EB]", pitching: "border-l-[#0B0F19]", strength: "border-l-emerald-500", speed: "border-l-amber-500", lesson: "border-l-sky-400" };

function Item({ c, onOpen, compact }) {
  const st = STATUS[c.status] || [c.status, "text-slate-500"];
  const full = c.type === "class" && c.registered >= c.capacity;
  return (
    <button data-testid={`sched-item-${c.key}`} onClick={() => c.type === "class" && onOpen(c)} className={`w-full text-left rounded-xl border border-slate-200 border-l-4 ${COLOR[c.program_type] || "border-l-slate-300"} bg-white px-3 py-2 hover:bg-slate-50 transition-colors ${c.type !== "class" ? "cursor-default" : ""}`}>
      <span className="flex items-center justify-between gap-2 text-xs font-bold text-slate-500"><span>{c.time}{c.minutes ? ` · ${c.minutes}m` : ""}</span><span className={st[1]}>{st[0]}</span></span>
      <span className="block text-sm font-extrabold text-[#0B0F19] break-words">{c.title}{c.age_group ? ` · ${c.age_group}` : ""}{c.athlete_name ? ` · ${c.athlete_name}` : ""}</span>
      {!compact && <span className="block text-xs text-slate-500">{c.location}{c.coach ? ` · ${c.coach}` : ""}</span>}
      {c.type === "class" && (
        <span className="mt-1 flex flex-wrap gap-x-3 text-xs font-semibold text-slate-600">
          <span className={full ? "text-red-600" : ""}>{c.registered}/{c.capacity}{full ? " full" : ""}</span>
          {!compact && <span>4-wk {c.four_week} · drop-in {c.dropin}{c.trial ? ` · trial ${c.trial}` : ""}</span>}
          {c.unsigned > 0 && <span className="text-amber-600">{c.unsigned} unsigned</span>}
        </span>
      )}
    </button>
  );
}

function Summary({ s }) {
  const cells = [["Today's classes", s.today], ["Next 7 days", s.next7], ["Athletes this week", s.athletes_week], ["Open spots (7 days)", s.spots_open], ["Outstanding waivers", s.outstanding_waivers, s.outstanding_waivers > 0], ["Unfinished sessions", s.unfinished, s.unfinished > 0]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {cells.map(([l, v, warn]) => (
        <div key={l} data-testid={`ops-sum-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`${card} ${warn ? "border-amber-300 bg-amber-50" : ""}`}>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{l}</p><p className="mt-1 text-2xl font-extrabold tabular-nums">{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function ScheduleBoard({ api }) {
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(null);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);
  const range = useMemo(() => {
    if (!anchor) return null;
    if (mode === "day") return [anchor, anchor];
    if (mode === "week") return [monday(anchor), addDays(monday(anchor), 6)];
    if (mode === "month") { const m = `${anchor.slice(0, 7)}-01`; const st = monday(m); return [st, addDays(st, 41)]; }
    return [anchor, addDays(anchor, 27)];
  }, [mode, anchor]);
  useEffect(() => { api.get("/admin/schedule", range ? { start: range[0], end: range[1] } : {}).then((x) => { setD(x); if (!anchor) setAnchor(x.today); }); }, [api, range, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = (n) => setAnchor((a) => (mode === "month" ? `${addDays(`${a.slice(0, 7)}-15`, n * 30).slice(0, 7)}-01` : addDays(a, n * (mode === "day" ? 1 : mode === "week" ? 7 : 28))));
  if (!d) return <div className={panel}><Loader2 className="animate-spin text-slate-400" /></div>;
  const byDay = (day) => d.items.filter((c) => c.date === day);
  const days = range ? Array.from({ length: mode === "day" ? 1 : mode === "week" ? 7 : 42 }, (_, i) => addDays(range[0], i)) : [];
  return (
    <div data-testid="schedule-board" className={panel}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight">Training week</h2>
        <div className="flex flex-wrap items-center gap-2">
          {["day", "week", "month", "list"].map((m) => <button key={m} data-testid={`sched-mode-${m}`} onClick={() => setMode(m)} className={btn(mode === m)}>{m[0].toUpperCase() + m.slice(1)}</button>)}
          <button data-testid="sched-prev" onClick={() => step(-1)} className="rounded-full border border-slate-300 p-2"><ChevronLeft size={16} /></button>
          <button data-testid="sched-today" onClick={() => setAnchor(d.today)} className="rounded-full border border-slate-300 px-3 py-2 text-sm font-bold">Today</button>
          <button data-testid="sched-next" onClick={() => step(1)} className="rounded-full border border-slate-300 p-2"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="mt-5"><Summary s={d.summary} /></div>
      {(d.unfinished.length > 0 || d.unscheduled.length > 0) && (
        <div data-testid="sched-attention" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <p className="flex items-center gap-2 font-bold text-amber-700"><AlertTriangle size={14} /> Needs attention</p>
          {d.unfinished.slice(0, 6).map((c) => <button key={c.key} onClick={() => setOpen(c)} className="block mt-1 text-left text-amber-800 hover:underline">{dlong(c.date)} · {c.title} {c.age_group} — not marked completed</button>)}
          {d.unscheduled.map((p) => <p key={p.program_id} className="mt-1 text-amber-800">{p.title} {p.age_group} — open for registration but has no class dates</p>)}
        </div>
      )}
      <p className="mt-5 text-sm font-bold text-slate-500">{range && (range[0] === range[1] ? dlong(range[0]) : `${dlong(range[0])} – ${dlong(range[1])}`)}</p>
      {mode === "list" ? (
        <div data-testid="sched-list" className="mt-3 space-y-2">
          {!d.items.length && <p className="text-sm text-slate-500">Nothing scheduled in these four weeks.</p>}
          {d.items.map((c) => <div key={c.key} className="grid sm:grid-cols-[110px_1fr] gap-2 items-start"><span className="text-sm font-bold text-slate-500 pt-2">{dlong(c.date)}</span><Item c={c} onOpen={setOpen} /></div>)}
        </div>
      ) : (
        <div className={mode === "day" ? "mt-3" : "mt-3 overflow-x-auto"}><div className={`grid gap-2 ${mode === "day" ? "grid-cols-1" : "grid-cols-1 md:grid-cols-7 md:min-w-[1050px]"}`}>
          {days.map((day) => {
            const out = mode === "month" && day.slice(0, 7) !== anchor.slice(0, 7);
            return (
              <div key={day} data-testid={`sched-day-${day}`} className={`rounded-2xl border p-2 ${mode === "month" ? "min-h-[110px]" : "min-h-[160px]"} ${day === d.today ? "border-[#2563EB] bg-[#2563EB]/[0.03]" : "border-slate-200"} ${out ? "opacity-40" : ""}`}>
                <p className="px-1 pb-1 text-xs font-bold uppercase tracking-wider text-slate-400">{dlong(day)}</p>
                <div className="space-y-1.5">{byDay(day).map((c) => <Item key={c.key} c={c} onOpen={setOpen} compact={mode === "month"} />)}</div>
              </div>
            );
          })}
        </div></div>
      )}
      <ClassDrawer api={api} item={open} onClose={() => setOpen(null)} onChanged={() => setTick((t) => t + 1)} />
    </div>
  );
}
